/* Vendor profile slideshow — supports mixed image/video items. */
import { useState, useEffect, useCallback, useRef } from "react";
import { ChevronLeft, ChevronRight, Play, Volume2, VolumeX } from "lucide-react";
import { MediaFavoriteHeart } from "@/lib/favorites";

export const Slideshow = ({ images, alt, orientation = "landscape", vendor_slug }) => {
  // Normalise: string -> {type:'image', src}; object passes through
  const items = images.map((it) =>
    typeof it === "string" ? { type: "image", src: it } : it
  );
  const [index, setIndex] = useState(0);
  const [muted, setMuted] = useState(true);
  const activeVideoRef = useRef(null);
  const count = items.length;

  const goTo = useCallback(
    (i) => setIndex(((i % count) + count) % count),
    [count]
  );
  const next = useCallback(() => goTo(index + 1), [index, goTo]);
  const prev = useCallback(() => goTo(index - 1), [index, goTo]);

  const current = items[index];
  const isVideo = current && current.type === "video";

  // Auto-advance: 5.5s for images. Videos advance only when they finish (onEnded).
  useEffect(() => {
    if (count <= 1) return undefined;
    if (isVideo) return undefined;
    const id = setTimeout(() => {
      setIndex((i) => (i + 1) % count);
    }, 5500);
    return () => clearTimeout(id);
  }, [index, count, isVideo]);

  // Re-apply mute state when the active video element mounts (after slide change).
  useEffect(() => {
    if (activeVideoRef.current) {
      activeVideoRef.current.muted = muted;
      activeVideoRef.current.play().catch(() => {});
    }
  }, [muted, index]);

  const isPortrait = orientation === "portrait";
  const containerClass = isPortrait
    ? "relative w-full mx-auto overflow-hidden bg-[var(--ld-ink)] aspect-[9/16] max-w-[420px] sm:max-w-[480px]"
    : "relative w-full overflow-hidden bg-[var(--ld-ink)] aspect-[16/10] sm:aspect-[16/9]";

  return (
    <div data-testid="vendor-slideshow" className={containerClass}>
      {items.map((item, i) => {
        const active = i === index;
        const baseClass =
          "absolute inset-0 w-full h-full object-cover transition-opacity duration-[1100ms] ease-out " +
          (active ? "opacity-100" : "opacity-0");
        if (item.type === "video") {
          return active ? (
            <video
              key={`v-${item.src}`}
              ref={activeVideoRef}
              src={item.src}
              poster={item.poster}
              autoPlay
              muted={muted}
              playsInline
              preload="metadata"
              onEnded={next}
              className={baseClass}
            />
          ) : (
            <img
              key={`vp-${item.src}`}
              src={item.poster}
              alt={`${alt} — ${i + 1}`}
              className={baseClass}
            />
          );
        }
        return (
          <img
            key={`i-${item.src}`}
            src={item.src}
            alt={`${alt} — ${i + 1}`}
            loading={i === 0 ? "eager" : "lazy"}
            className={baseClass}
          />
        );
      })}
      <div
        aria-hidden
        className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none"
      />
      {count > 1 && (
        <>
          <button
            type="button"
            onClick={prev}
            data-testid="slideshow-prev"
            aria-label="Previous"
            className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 inline-flex h-11 w-11 items-center justify-center bg-[var(--ld-ivory)]/90 text-[var(--ld-ink)] border border-[var(--ld-ink)]/10 hover:bg-[var(--ld-ivory)] transition-colors"
          >
            <ChevronLeft size={18} strokeWidth={1.6} />
          </button>
          <button
            type="button"
            onClick={next}
            data-testid="slideshow-next"
            aria-label="Next"
            className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 inline-flex h-11 w-11 items-center justify-center bg-[var(--ld-ivory)]/90 text-[var(--ld-ink)] border border-[var(--ld-ink)]/10 hover:bg-[var(--ld-ivory)] transition-colors"
          >
            <ChevronRight size={18} strokeWidth={1.6} />
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
            {items.map((it, i) => (
              <button
                key={(it && (it.src || it.url)) || i}
                type="button"
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`}
                data-testid={`slideshow-dot-${i}`}
                className={
                  "h-1.5 transition-all duration-500 " +
                  (i === index
                    ? "w-8 bg-[var(--ld-gold)]"
                    : "w-3 bg-white/60 hover:bg-white")
                }
              />
            ))}
          </div>
          {isVideo && (
            <div className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-2 py-1 bg-black/55 text-white/90 backdrop-blur-sm">
              <Play size={11} strokeWidth={2} fill="currentColor" />
              <span className="text-[10px] tracking-[0.2em] uppercase">
                Reel
              </span>
            </div>
          )}
        </>
      )}

      {isVideo && (
        <button
          type="button"
          data-testid="slideshow-mute-toggle"
          onClick={(e) => {
            e.stopPropagation();
            setMuted((m) => !m);
          }}
          aria-label={muted ? "Unmute video" : "Mute video"}
          className="absolute bottom-4 right-4 inline-flex h-9 w-9 items-center justify-center bg-black/60 text-white/90 backdrop-blur-sm hover:bg-black/80 transition-colors"
        >
          {muted ? (
            <VolumeX size={15} strokeWidth={1.8} />
          ) : (
            <Volume2 size={15} strokeWidth={1.8} />
          )}
        </button>
      )}

      {current && vendor_slug && (
        <MediaFavoriteHeart
          vendor_slug={vendor_slug}
          media_url={current.src}
          media_type={current.type === "video" ? "video" : "image"}
          size={15}
          className="absolute top-4 right-4 h-9 w-9 z-10"
          testid={`grid-media-favorite-${vendor_slug}-${index}`}
        />
      )}
    </div>
  );
};
